import { TwentyOne } from 'game-logic';
import { TwentyOneRoundCreator } from './twentyone-round-creator';
import { Subject } from 'rxjs';
import { TwentyOneRemoteRoundParams } from './twentyone-remote-round-params';
import { TwentyOneLocalRoundParams } from './twentyone-local-round-params';

export class TwentyOneGameRunner {
  public get onGameSetupStart$() {
    return this.onGameSetupStart.asObservable();
  }
  public get onGameSetupComplete$() {
    return this.onGameSetupComplete.asObservable();
  }
  public get twentyOneLogicActionBus$() {
    return this.roundCreator.twentyOneLogicActionBus$;
  }
  public get logicEventQueue$() {
    return this.roundCreator.logicEventQueue$;
  }
  public get currentGame() {
    return this.roundCreator.currentRound?.currentGame;
  }

  private readonly gameListener: TwentyOne.TwentyOneListener;
  private readonly roundCreator: TwentyOneRoundCreator;
  private readonly onGameSetupStart: Subject<void>;
  private readonly onGameSetupComplete: Subject<void>;

  constructor(gameListener: TwentyOne.TwentyOneListener) {
    this.gameListener = gameListener;
    this.roundCreator = new TwentyOneRoundCreator(gameListener);
    this.onGameSetupStart = new Subject();
    this.onGameSetupComplete = new Subject();
  }

  public startNewRemoteRound(logicVersion: number) {
    this.roundCreator.startNewRound(
      TwentyOneRemoteRoundParams.create(this.gameListener, logicVersion),
    );

    this.onGameSetupStart.next();
    void this.newGameSetup();
  }

  public startExistingRemoteRound(moves: string[], logicVersion: number) {
    this.roundCreator.startNewRound(
      TwentyOneRemoteRoundParams.create(this.gameListener, logicVersion),
    );

    this.onGameSetupStart.next();
    void this.existingGameSetup(moves);
  }

  public startNewLocalRound(logicVersion: number, logicSeed?: number) {
    this.roundCreator.startNewRound(TwentyOneLocalRoundParams.create(this.gameListener, logicSeed));

    this.onGameSetupStart.next();
    void this.newGameSetup();
  }

  public clickStartGame() {
    this.gameListener.onStartGame();
  }

  public clickColumn(columnId: TwentyOne.PileId) {
    const game = this.currentGame;
    if (!game) {
      return;
    }
    game.moveToColumn(columnId);
  }

  public copyTestCase() {
    const game = this.currentGame;
    if (!game) {
      return;
    }
    if (game.dealer instanceof TwentyOne.LocalDealer) {
      navigator.clipboard.writeText(
        JSON.stringify({
          attestation: game.dealer.attestation(),
          moves: game.serializeCommandList(),
          analyticsData: game.getAnalyicsData(),
        }),
      );
    }
  }

  private async newGameSetup(): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    await game.startGame(false);

    this.onGameSetupComplete.next();
    this.roundCreator.currentRound?.onGameSetupAnimationFinished();
  }

  private async existingGameSetup(moves: string[]): Promise<void> {
    const game = this.roundCreator.currentRound?.currentGame;
    if (game === undefined) {
      return;
    }

    await game.startGame(true);

    await this.executeExistingMoves(game, moves, true);

    this.onGameSetupComplete.next();
    this.roundCreator.currentRound?.onGameSetupAnimationFinished();
  }

  private async executeExistingMoves(
    game: TwentyOne.TwentyOneGameLogic,
    moves: string[],
    instant: boolean,
  ) {
    for (const move of moves) {
      const command = TwentyOne.CommandListSerializer.moveToCommand(move);
      game.executeCommand(command, true, instant);
      await game.waitForAllTasksToBeCompleted();
    }
  }
}
